import { useEffect, useRef, type RefObject } from 'react';
import { FROG_HOP_KEYFRAMES, FROG_HOP_MS } from '@vd/types';
import { api } from '../../lib/api';

/**
 * The frog in the top bar hops once when a new ticket arrives (Jason, 18 Oct):
 * a small, cheerful sign that something landed, where nobody is reading.
 *
 * - One hop however many tickets arrive together, and none while one is
 *   playing or within `QUIET_MS` of the last.
 * - A ticket that arrives while the tab is hidden (someone pinning on another
 *   page) hops when the tab is shown again, so coming back after a capture
 *   is greeted by it.
 * - Nothing under reduced motion.
 *
 * The motion is the Web Animations API on the logo's button, so a hop can
 * start again without a class to take off and put back.
 */
const QUIET_MS = 1500;

export function useLogoHop(target: RefObject<HTMLElement | null>): void {
  const last = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const hop = () => {
      const el = target.current;
      if (!el || reduced() || typeof el.animate !== 'function') return;
      const now = Date.now();
      if (now - last.current < QUIET_MS) return;
      last.current = now;
      el.style.transformOrigin = '50% 100%';
      el.animate([...FROG_HOP_KEYFRAMES], { duration: FROG_HOP_MS });
    };
    const unsubscribe = api.subscribe((event) => {
      if (event.type !== 'ticket.created') return;
      if (document.hidden) pending.current = true;
      else hop();
    });
    const onVisible = () => {
      if (document.hidden || !pending.current) return;
      pending.current = false;
      // A beat after the tab shows, so the eye is on the page when it plays.
      window.setTimeout(hop, 350);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [target]);
}
